import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import AppContext from '../../../AppContext';
import { Exam, ExamDiploma, PassedParticipantsContext } from '../../../types';
import PassedParticipants from './PassedParticipants';
import InfoText from '../../../components/InfoText';
import ExamFormEntry from './ExamFormEntry';
import {
  EXAM_FORM_TYPE_TO_EXAM_FIELD_MAPPING,
  ExamFormState,
  ExamFormType,
} from '../types';

type DisplayMode = 'passed-participants' | 'form-entry';

interface ExamReportTabProps {
  exam: Exam;
  type: ExamFormType;
}

const getInitialDisplayMode = (
  examFormType: ExamFormType,
  examLines: ExamDiploma[],
) => {
  let passedParticipantsContext: PassedParticipantsContext | null = null;
  if (examFormType === ExamFormType.EXAMINER) {
    passedParticipantsContext = PassedParticipantsContext.examiner;
  }
  if (examFormType === ExamFormType.DELEGATE) {
    passedParticipantsContext = PassedParticipantsContext.delegate;
  }

  let displayMode: DisplayMode = 'form-entry';
  if (passedParticipantsContext !== null) {
    // determine whether the number of passed participants has already been registered
    const hasPassedParticipants =
      examLines.find((examLine: ExamDiploma) => {
        if (passedParticipantsContext === PassedParticipantsContext.examiner) {
          return examLine.numberPassedAccordingExaminer >= 1;
        }
        if (passedParticipantsContext === PassedParticipantsContext.delegate) {
          return examLine.numberPassedAccordingDelegate >= 1;
        }
        return false;
      }) !== undefined;

    if (!hasPassedParticipants) {
      displayMode = 'passed-participants';
    }
  }

  return {
    passedParticipantsContext,
    displayMode,
  };
};

const ExamReportTab = (props: ExamReportTabProps) => {
  const history = useHistory();
  const { roleViewManager } = useContext(AppContext);

  const isClosed = Boolean(props.exam.closedAt);
  const isSuperAdmin = roleViewManager.isSuperAdminView();

  const examFormEntry =
    props.exam[EXAM_FORM_TYPE_TO_EXAM_FIELD_MAPPING[props.type]];
  const examFormEntryMissing =
    examFormEntry === undefined || examFormEntry.state === ExamFormState.OPEN;

  const { passedParticipantsContext, displayMode } = getInitialDisplayMode(
    props.type,
    props.exam.examLines,
  );

  const [readOnly, setReadOnly] = useState<boolean>(() => {
    // a closed exam starts read-only; only super admins can switch to editing
    if (isClosed) {
      return true;
    }

    let roleAllowsEditing = false;
    let statusAllowsEditing = false;
    switch (props.type) {
      case 'aspirant':
        roleAllowsEditing =
          roleViewManager.isAdminView() || roleViewManager.isDelegateView();
        statusAllowsEditing =
          props.exam.aspirantFormEntry === undefined ||
          props.exam.aspirantFormEntry.state === ExamFormState.OPEN;
        break;
      case 'delegate':
        roleAllowsEditing =
          roleViewManager.isAdminView() || roleViewManager.isDelegateView();
        statusAllowsEditing =
          props.exam.delegateFormEntry === undefined ||
          props.exam.delegateFormEntry.state === ExamFormState.OPEN;
        break;
      case 'examiner':
        roleAllowsEditing =
          roleViewManager.isAdminView() ||
          roleViewManager.isExaminerView() ||
          roleViewManager.isSwimmingLessonProviderView();
        statusAllowsEditing =
          props.exam.examinerFormEntry === undefined ||
          props.exam.examinerFormEntry.state === ExamFormState.OPEN;
        break;
    }

    return !roleAllowsEditing || !statusAllowsEditing;
  });

  const reload = () => {
    history.go(0);
  };

  return (
    <>
      {passedParticipantsContext !== null && (
        <PassedParticipants
          context={passedParticipantsContext}
          exam={props.exam}
          canEdit={
            isClosed
              ? isSuperAdmin
              : (displayMode === 'passed-participants' && !readOnly) ||
                roleViewManager.isAdminView()
          }
          onSave={() => reload()}
        />
      )}
      {displayMode === 'passed-participants' &&
        (isClosed && !isSuperAdmin ? (
          <InfoText>
            Het rapport is niet beschikbaar, want dit examen is afgesloten.
          </InfoText>
        ) : (
          <InfoText>
            Registreer eerst het aantal geslaagde kandidaten om het rapport in
            te vullen
          </InfoText>
        ))}
      {displayMode === 'form-entry' &&
        (isClosed && !isSuperAdmin && examFormEntryMissing ? (
          <InfoText>
            Het rapport is niet beschikbaar, want dit examen is afgesloten.
          </InfoText>
        ) : (
          <ExamFormEntry
            exam={props.exam}
            type={props.type}
            readOnly={readOnly}
            setReadOnly={setReadOnly}
          />
        ))}
    </>
  );
};

export default ExamReportTab;
